import { useEffect } from "react";
import { OG_IMAGE_PATH, ROUTE_META, SITE_ORIGIN as ORIGIN } from "./siteMeta";

export interface PageMeta {
  title: string;
  description: string;
  /** Absolute path of the current page, e.g. "/marketplace/apps/app-contract". */
  path: string;
  /** Optional Open Graph type; defaults to "website". */
  ogType?: string;
}

/** Public origin used for canonical + Open Graph URLs. */
export const SITE_ORIGIN = ORIGIN;

/**
 * Metadata for a public route, from the same table the build writes into the
 * served HTML, so the crawler's copy and the router's copy cannot drift.
 */
export function routeMeta(path: keyof typeof ROUTE_META | string): PageMeta {
  const meta = ROUTE_META[path];
  if (!meta) throw new Error(`No metadata registered for ${path}`);
  return { ...meta, path };
}

function upsertMeta(selector: string, attrs: Record<string, string>): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
}

/**
 * Per-route document metadata for the public storefront. The app is a SPA, so
 * every marketing route would otherwise share the shell's single title and
 * description, and crawlers would see one canonical URL for the whole site.
 */
export function usePageMeta(meta: PageMeta | null): void {
  const { title, description, path, ogType } = meta ?? {};
  useEffect(() => {
    if (!title || !description || !path) return;
    const url = `${SITE_ORIGIN}${path}`;
    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: ogType ?? "website" });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: `${SITE_ORIGIN}${OG_IMAGE_PATH}` });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: `${SITE_ORIGIN}${OG_IMAGE_PATH}` });
    upsertCanonical(url);
  }, [title, description, path, ogType]);
}
